import { useState, useMemo } from 'react'

export function useClientSort<T>(
  items: T[],
  defaultKey: string = 'patientName',
  defaultDir: 'asc' | 'desc' = 'asc'
) {
  const [sortKey, setSortKey] = useState(defaultKey)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultDir)

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('asc') }
  }

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    return [...items].sort((a, b) => {
      const aVal = (a as Record<string, unknown>)[sortKey]
      const bVal = (b as Record<string, unknown>)[sortKey]
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return 1
      if (bVal == null) return -1
      if (typeof aVal === 'string') return aVal.localeCompare(bVal as string) * dir
      if (typeof aVal === 'boolean') return ((aVal ? 1 : 0) - ((bVal as boolean) ? 1 : 0)) * dir
      return ((aVal as number) - (bVal as number)) * dir
    })
  }, [items, sortKey, sortDir])

  return { sorted, sortKey, sortDir, handleSort }
}

export function useClientFilter<T>(
  items: T[],
  filter: string,
  searchKeys: (keyof T)[]
) {
  return useMemo(() => {
    if (!filter) return items
    const q = filter.toLowerCase()
    return items.filter(item =>
      searchKeys.some(key => {
        const val = item[key]
        return typeof val === 'string' && val.toLowerCase().includes(q)
      })
    )
  }, [items, filter, searchKeys])
}
